import { compact, partition } from "lodash";
import { GetOrderResponse, PortalNotificationReference, PortalNotificationType } from "../types";
import { isAdhocPortalOrder } from "./isAdhocPortalOrder";

function sortByDate(a: string, b: string) {
  return (Date.parse(b) || 0) - (Date.parse(a) || 0)
}

function sortByName(a: string, b: string) {
  return (a).localeCompare(b);
}

export function checkIfPastDue(dueDate: string) {
  return new Date(dueDate) < new Date()
}

export function isMessageNotificationType(type: PortalNotificationType) {
  return type === 'Message'
}

export function isTaskNotificationType(type: PortalNotificationType) {
  return type !== 'Message'
}

/**
 * Sort the given notifications and return a data structure grouping them into
 * the various types, with description text for the ui.
 */
export function getNotificationsInfo(_notifications: PortalNotificationReference[], uiContent: any, orderData: GetOrderResponse) {
  const { TaskText, TasksText, MessageText, MessagesText } = uiContent

  const notifications = compact(_notifications)

  const sortedNotifications: PortalNotificationReference[] = notifications.sort((a, b) => {
    const dateSort = sortByDate(a.CreatedDateTime, b.CreatedDateTime)
    if (dateSort) return dateSort
    return sortByName(a.NotificationTitle, b.NotificationTitle)
  })

  // Tasks are any non-Message notification.
  const tasks = sortedNotifications.filter(n => isTaskNotificationType(n.Type))
  const wiringInstructionsTaskRef = sortedNotifications.find(n => n.Type === 'WiringInstructionsTask')
  const messages = sortedNotifications.filter(n => isMessageNotificationType(n.Type))

  let [unreadMessages, readMessages] = partition(messages, ['ReadReceipt', false])
  const pastDue = !isAdhocPortalOrder(orderData.Order) && orderData && orderData.Order && orderData.Order?.CashToCloseDetails && checkIfPastDue(orderData.Order.CashToCloseDetails.dueDate)

  //CashToCloseTask that is past due will be marked as a complete task here.
  const [incompleteTasks, completeTasks] = partition(
    tasks,
    task =>
      task.IsComplete === false && !(task.Type === 'CashToCloseTask' && pastDue)
  )

  const unreadMessagesCount = unreadMessages.length
  const incompleteTasksCount = incompleteTasks.length
  const messagesCountText = unreadMessagesCount ? `You have ${unreadMessages.length} new ${unreadMessages.length > 1 ? MessagesText : MessageText}!` : ''
  const tasksCountText = incompleteTasksCount ? `You have ${incompleteTasks.length} ${incompleteTasks.length > 1 ? TasksText : TaskText} to complete!` : ''

  const hasWiringInstructionsTask = !!wiringInstructionsTaskRef
  const wiringInstructionsTaskIsIncomplete = hasWiringInstructionsTask && !wiringInstructionsTaskRef.IsComplete
  const wiringInstructionsTaskText = wiringInstructionsTaskIsIncomplete
    ? "New instructions available!"
    : ""
  ;

  return {
    notifications,
    hasMessages: messages.length > 0,
    messagesCountText,
    messages,
    unreadMessages,
    readMessages,

    hasTasks: tasks.length > 0,
    tasksCountText,
    tasks,
    incompleteTasks,
    completeTasks,

    hasWiringInstructionsTask,
    wiringInstructionsTaskRef,
    wiringInstructionsTaskText,
  };
}


export type NotificationsInfo = ReturnType<typeof getNotificationsInfo>
